import { useEffect, useRef, useState } from 'react'
import { Box, Button, ButtonLink, Card, Group, Segmented, Text, TextInput, Title } from '@grovus/design-system'
import { createCompany, fetchCompanies } from '../data/companies'
import { foldName } from '../data/clients'
import { AUTH_COPY, LANG_OPTIONS, type Dict, type Lang } from '../i18n'
import { metaGuideUrl } from '../lib/guideUrl'
import type { Theme } from '../lib/preferences'
import { AuthLayout } from '../auth/AuthLayout'
import { fetchSession } from '../auth/api'
import { authGeneration } from '../auth/session'
import { REFRESH_MS } from '../auth/refreshQueue'

interface Props {
  t: Dict
  lang: Lang
  setLang: (lang: Lang) => void
  theme: Theme
  toggleTheme: () => void
  onSignOut: () => Promise<'ok' | 'error'>
  /**
   * The session's `demoCompanyPending`: the next create makes the user's demo company, so
   * step 1 offers Skip, which creates only that (GRO-131, D2).
   */
  demoCompanyPending?: boolean
  /** Called once, after `createCompany` succeeds, with the created company's id. */
  onDone: (companyId: string) => void
  /**
   * The boundary's entry into the created company after `onDone`: `pending` while it
   * reads the session and the companies, `failed` when that read failed. Retry repeats
   * only the read, never the creation (plan, Task 7a).
   */
  completion?: 'pending' | 'failed' | null
  onRetryCompletion?: () => void
}

type Step = 1 | 2

const STEPS: Array<{ step: Step; labelKey: 'frStepCompany' | 'frStepConnect' }> = [
  { step: 1, labelKey: 'frStepCompany' },
  { step: 2, labelKey: 'frStepConnect' },
]

/**
 * Two steps to a company (GRO-19, GRO-106): the client's name, then connect. Finish and
 * step 2's Skip both call `POST /api/companies` (GRO-39) with the name and hand the
 * created id to the boundary, which enters the shell on it once the session shows the
 * membership. On a first run step 1 also offers Skip, which posts an empty name and enters
 * the demo company (GRO-131). There is no OAuth grant to start; "Connect Meta Ads" opens
 * the customer guide to Meta portfolio access (GRO-98) in a new tab, so the wizard keeps
 * its place.
 */
export function FirstRun({ t, lang, setLang, theme, toggleTheme, onSignOut, demoCompanyPending = false, onDone, completion = null, onRetryCompletion }: Props) {
  const [step, setStep] = useState<Step>(1)
  const [companyName, setCompanyName] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitFailed, setSubmitFailed] = useState(false)
  // A duplicate answer whose company could not be found yet: Retry repeats the lookup,
  // never the create (GRO-130, D6).
  const [recheck, setRecheck] = useState<string | null>(null)
  const [canSkipToDemo, setCanSkipToDemo] = useState(demoCompanyPending)
  // A Skip whose outcome is unknown, and whose session reread failed: Retry repeats the
  // reread, never the create (GRO-131, D2).
  const [skipRecheck, setSkipRecheck] = useState(false)
  const [reserved, setReserved] = useState(false)

  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => { mountedRef.current = false }
  }, [])

  const canAdvance = step === 1 ? companyName.trim() !== '' : true
  const busy = submitting || completion !== null

  /**
   * A `409 duplicate_name` on first run means an earlier `POST` with this name committed
   * and its answer was lost: the user has no other company to clash with. Find that
   * company and hand it to the boundary as if this create had answered with it; never ask
   * for another name, which would make the second company the rule exists to prevent.
   */
  async function adoptExisting(name: string) {
    setSubmitting(true)
    setRecheck(null)
    const generation = authGeneration()
    // Bounded like the boundary's own reads: a lookup that stalls ends in the Retry line
    // rather than leaving every control disabled.
    const deadline = new AbortController()
    const timer = setTimeout(() => deadline.abort(), REFRESH_MS)
    const result = await fetchCompanies(fetch, deadline.signal)
    clearTimeout(timer)
    if (!mountedRef.current) return
    const existing = generation === authGeneration() && result.status === 'ok'
      ? result.companies.find((company) => foldName(company.name) === foldName(name))
      : undefined
    if (existing === undefined) {
      setSubmitting(false)
      setRecheck(name)
      return
    }
    onDone(existing.id)
  }

  /**
   * After a Skip without a clear answer, the session says what happened: a demo company
   * means the create committed, `demoCompanyPending` means it did not and Skip can be
   * sent again, and neither means there is no demo company to make.
   */
  async function recheckSkip() {
    setSubmitting(true)
    setSkipRecheck(false)
    const generation = authGeneration()
    const session = await fetchSession()
    if (!mountedRef.current) return
    setSubmitting(false)
    if (generation !== authGeneration() || session.status !== 'authenticated') {
      setSkipRecheck(true)
      return
    }
    const { demoCompanyId, demoCompanyPending: pending } = session.user
    if (demoCompanyId != null) {
      onDone(demoCompanyId)
      return
    }
    setCanSkipToDemo(pending === true)
    setSubmitFailed(pending === true)
  }

  async function skipToDemo() {
    if (busy) return
    const generation = authGeneration()
    setSubmitting(true)
    setSubmitFailed(false)
    setReserved(false)
    const result = await createCompany('')
    if (!mountedRef.current) return
    if (generation === authGeneration() && result.status === 'ok') {
      onDone(result.company.id)
      return
    }
    await recheckSkip()
  }

  async function finish() {
    if (busy) return
    const trimmedCompany = companyName.trim()
    const generation = authGeneration()
    setSubmitting(true)
    setSubmitFailed(false)
    const result = await createCompany(trimmedCompany)
    // A sign-out (or expiry) that lands while this request is in flight must not resurrect
    // authenticated work after it: no local write, no onDone, whether or not the request
    // itself came back `ok` (implement-gate F1 - the created company is real and not undone
    // by discarding this result, but nothing here treats it as active).
    if (!mountedRef.current) return
    if (generation !== authGeneration()) {
      // The only way the boundary leaves this mounted after the generation moves on is a
      // sign-out request that aborted this one but then itself failed (AuthBoundary's
      // handleSignOut resets auth work before the request settles): the session is still
      // ours, just under a new generation. Recover the same way any other failure does,
      // rather than leaving Finish/Skip/Back disabled with no way out (implement-gate F2).
      setSubmitting(false)
      setSubmitFailed(true)
      return
    }
    if (result.status === 'duplicate') {
      await adoptExisting(trimmedCompany)
      return
    }
    if (result.status === 'reserved') {
      setSubmitting(false)
      setReserved(true)
      setStep(1)
      return
    }
    if (result.status !== 'ok') {
      setSubmitting(false)
      setSubmitFailed(true)
      return
    }
    onDone(result.company.id)
  }

  return (
    <AuthLayout
      maxWidth={452}
      topRight={(
        <>
          <Segmented
            value={lang}
            onChange={(value) => setLang(value as Lang)}
            data={LANG_OPTIONS}
          />
          <Button variant="default" onClick={toggleTheme}>
            {theme === 'light' ? t.dark : t.light}
          </Button>
        </>
      )}
    >
      <Card>
        <Group gap={14} mb="md" aria-label={t.frStepsLabel}>
          {STEPS.map(({ step: s, labelKey }) => (
            <Text
              key={s}
              fz="xs"
              fw={step === s ? 700 : 500}
              className={step === s ? undefined : 'grv-muted'}
              aria-current={step === s ? 'step' : undefined}
            >
              {t[labelKey]}
            </Text>
          ))}
        </Group>

        {step === 1 && (
          <Box>
            <Title order={2} fz={18}>{t.frStepCompany}</Title>
            <TextInput
              mt="md"
              label={t.frCompanyLabel}
              placeholder={t.frCompanyPlaceholder}
              value={companyName}
              onChange={(e) => { setCompanyName(e.currentTarget.value); setReserved(false) }}
              error={reserved ? t.frReservedName : undefined}
            />
          </Box>
        )}

        {step === 2 && (
          <Box>
            <Title order={2} fz={18}>{t.frStepConnect}</Title>
            <ButtonLink mt="md" href={metaGuideUrl(lang)} target="_blank" rel="noopener">{t.frConnectMeta}</ButtonLink>
            <Text mt="sm" fz="sm">TikTok - {t.notConnectedYet}</Text>
            <Text fz="sm">Google - {t.notConnectedYet}</Text>
            {/* The step that offers a connection says where the sample figures are. */}
            <Text mt="md" fz="sm" className="grv-muted" data-testid="first-run-demo-hint">
              {t.frDemoHint}
            </Text>
          </Box>
        )}

        {(submitFailed || skipRecheck || completion === 'failed') && (
          <Text mt="md" fz="sm" role="alert" c="bad.7">{AUTH_COPY[lang].service_unavailable}</Text>
        )}
        {recheck !== null && completion !== 'failed' && (
          <Text mt="md" fz="sm" role="alert" data-testid="fr-duplicate-check">{t.frDuplicateCheck}</Text>
        )}

        <Group mt="lg" justify="space-between">
          <Group gap={8}>
            {step > 1 && (
              <Button variant="default" disabled={busy} onClick={() => setStep((s) => (s - 1) as Step)}>{t.frBack}</Button>
            )}
            <Button variant="subtle" onClick={() => { void onSignOut() }}>{t.signOut}</Button>
          </Group>
          <Group gap={8}>
            {completion === 'failed' ? (
              <Button onClick={onRetryCompletion}>{t.retry}</Button>
            ) : recheck !== null ? (
              <Button disabled={busy} onClick={() => { void adoptExisting(recheck) }}>{t.retry}</Button>
            ) : skipRecheck ? (
              <Button disabled={busy} onClick={() => { void recheckSkip() }}>{t.retry}</Button>
            ) : (
              <>
                {step === 1 && canSkipToDemo && (
                  <Button variant="default" disabled={busy} onClick={() => { void skipToDemo() }}>{t.frSkip}</Button>
                )}
                {step === 2 && <Button variant="default" disabled={busy} onClick={finish}>{t.frSkip}</Button>}
                {step < 2 ? (
                  <Button disabled={!canAdvance} onClick={() => setStep((s) => (s + 1) as Step)}>{t.frNext}</Button>
                ) : (
                  <Button disabled={busy} onClick={finish}>{t.frFinish}</Button>
                )}
              </>
            )}
          </Group>
        </Group>
      </Card>
    </AuthLayout>
  )
}
